import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CButton, CCard, CCardBody, CCol, CRow } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCloudUpload,
  cilImagePlus,
  cilPeople,
  cilSpreadsheet,
  cilTrash,
} from '@coreui/icons'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Table from './Table'
import AddForm from './AddForm'
import SquadPickedTable from './SquadPickedTable'
import FixtureService from 'src/service/FixtureService'
import TeamPlayerService from 'src/service/TeamPlayerService'
import ScorecardService from 'src/service/ScorecardService'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import Notify from '../Notify'
import ImportPlayerSquad from '../Player/ImportPlayerSquad'
import SquadCsvImport from './SquadCsvImport'
import { CollapsibleImportCard } from 'src/components/common/ImportWizard'

// The player listing is unpaginated, so one generous page covers a club's whole pool and the
// picker can search within it in memory.
const PLAYER_FETCH_LIMIT = 500

const SELECT_TAB = 'select'
const LIST_TAB = 'list'
const IMAGE_TAB = 'image'

const TABS = [
  { key: SELECT_TAB, label: 'Select Players' },
  { key: LIST_TAB, label: 'Player List' },
  { key: IMAGE_TAB, label: 'Import from Image' },
]

const TAB_HINTS = {
  [SELECT_TAB]: 'Pick several players at once from the dropdown, then submit them together.',
  [LIST_TAB]: 'Browse every player and add or remove them one at a time.',
  [IMAGE_TAB]: 'Upload a team sheet and we will read the names off it and match them to players.',
}

const formatDate = (date) => (date ? moment(date).format('D.MM.YYYY') : '—')
const formatTime = (time) => (time ? moment(time, ['HH:mm:ss', 'HH:mm']).format('h:mm A') : '—')

const TeamPlayer = () => {
  const urlParams = useParams()
  const navigate = useNavigate()
  const fixtureId = urlParams.fixtureId
  const teamId = urlParams.teamId

  const [fixtureDetails, setFixtureDetails] = useState({})
  const [gameType, setGameType] = useState()
  const [players, setPlayers] = useState([])
  const [squad, setSquad] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(SELECT_TAB)
  const [importOpen, setImportOpen] = useState(false)
  const [scorecardColumns, setScorecardColumns] = useState([])
  const [columnsLoading, setColumnsLoading] = useState(false)
  const [importInnings, setImportInnings] = useState(1)

  useEffect(() => {
    FixtureService.getFixtureDetail(fixtureId, teamId).then((res) => {
      if (res.status === 200) {
        setFixtureDetails(res.data)
      }
    })
    CommonService.getGameType().then((res) => {
      if (res.status === 200) {
        setGameType(res.data)
      }
    })
  }, [fixtureId, teamId])

  // One request feeds both panels: `data` is every selectable player, `picked_player` is the
  // squad as it stands. Everything that changes the squad calls this again.
  const loadPlayers = useCallback(() => {
    setLoading(true)
    TeamPlayerService.getTeamPlayer(
      0,
      PLAYER_FETCH_LIMIT,
      1,
      new URLSearchParams(),
      fixtureId,
      teamId,
    )
      .then((res) => {
        setPlayers(res?.data?.data ?? [])
        setSquad(res?.picked_player ?? [])
      })
      .catch(() => ToastComponent('Unable to load players.', 'error'))
      .finally(() => setLoading(false))
  }, [fixtureId, teamId])

  useEffect(() => {
    loadPlayers()
  }, [loadPlayers])

  // The import reads a file's column headings against this game's own scorecard columns, which
  // is where "RS" becomes `rs` and "4S" becomes `fours`. Fetched from getFixtureAttributes
  // rather than from the scorecard itself: asking for the scorecard creates its rows as a side
  // effect, and opening a panel is no reason to do that.
  useEffect(() => {
    if (!importOpen || scorecardColumns.length || columnsLoading || gameType === undefined) return
    setColumnsLoading(true)
    FixtureService.getFixtureAttributes(gameType === 3 ? 2 : 1)
      .then((res) => {
        setScorecardColumns((res?.data || []).filter((column) => Number(column.is_enabled) === 1))
      })
      .catch(() => ToastComponent('Could not load the scorecard columns for this game.', 'error'))
      .finally(() => setColumnsLoading(false))
  }, [importOpen, scorecardColumns.length, columnsLoading, gameType])

  // Are you sure want modal
  const [handleYes, setHandleYes] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [handleNo, setHandleNo] = useState(false)

  const handleCancel = () => setShowConfirm(false)

  const handleConfirm = () => {
    deleteSquad()
    return setShowConfirm(false)
  }

  const deleteSquad = () => {
    TeamPlayerService.deleteSquad(fixtureId, teamId).then((res) => {
      if (res.status === 200) {
        toast.dismiss()
        ToastComponent(res.message, 'success')
        loadPlayers()
      }
    })
  }

  // The spreadsheet import adds to the squad through the same endpoint the Select Players panel
  // uses, so both paths get the same skip-what-is-already-there behaviour server-side.
  //
  // When the file carries scores as well, they go in a second pass, and the order matters: the
  // scorecard rows a save writes into are created lazily by the scorecard endpoint, and only for
  // the players who are in the squad the first time it is asked. So the squad is completed first,
  // then the scorecard is read - creating a row per player - then the figures are written onto
  // the rows that came back.
  const importSquad = async (playerIds, scoreRows) => {
    try {
      if (playerIds.length) {
        const res = await TeamPlayerService.saveMultiPlayer({
          playerId: playerIds,
          fixtureId,
          teamId,
        })
        if (res.status !== 200) {
          return { success: false, errors: [res.message || 'Could not add these players.'] }
        }
      }

      if (!scoreRows.length) {
        toast.dismiss()
        ToastComponent(
          `${playerIds.length} player${playerIds.length === 1 ? '' : 's'} added to the squad.`,
          'success',
        )
        setImportOpen(false)
        loadPlayers()
        return { success: true }
      }

      const saved = await saveImportedScores(scoreRows)
      loadPlayers()
      if (saved.success) setImportOpen(false)
      return saved
    } catch (e) {
      loadPlayers()
      return { success: false, errors: ['Something went wrong. Please try again.'] }
    }
  }

  // save-scorecard replaces the whole innings, blanking any stat column and any fixture field
  // missing from its payload, so what it is handed is the innings just read back with the
  // imported figures merged into it - never a payload built from the file alone.
  const saveImportedScores = async (scoreRows) => {
    const detail = await ScorecardService.scorecardDetail(fixtureId, importInnings, teamId)
    if (detail.status !== 200) {
      return {
        success: false,
        errors: ['The players were added, but the scorecard could not be read.'],
      }
    }

    const valuesByPlayer = new Map(scoreRows.map((r) => [String(r.playerId), r.values]))
    const written = new Set()

    const data = (detail.data || []).map((row) => {
      const values = valuesByPlayer.get(String(row.player_id))
      if (!values) return { ...row }
      written.add(String(row.player_id))
      return { ...row, ...values }
    })

    // A fixture whose scorecard already existed gets no rows for players added afterwards, so
    // their figures have nowhere to go. Name them rather than report a clean import.
    const missing = scoreRows
      .filter((r) => !written.has(String(r.playerId)))
      .map((r) => playerNameById.get(String(r.playerId)) || r.playerId)

    const fixture = detail.fixtureDetails || {}
    const payload = {
      data,
      fixtureId,
      inning: importInnings,
      status: fixture.status,
      scorcard_link_name: fixture.scorcard_link_name,
      scorcard_link_url: fixture.scorcard_link_url,
      fall_of_wickets: fixture.fall_of_wickets,
      match_report: fixture.match_report,
    }
    // Passed back only when they hold something: save-scorecard overwrites the fixture's bonus
    // points with whatever it is handed, and an import must not clear them.
    if (fixture.potm?.length) payload.potm = fixture.potm
    if (fixture.potm_match_status) payload.potm_match_status = fixture.potm_match_status

    const res = await ScorecardService.saveScorecard(payload)
    if (res.status !== 200) {
      return {
        success: false,
        errors: [res.message || 'The players were added, but their scores could not be saved.'],
      }
    }

    if (missing.length) {
      return {
        success: false,
        errors: [
          `Scores were saved for ${written.size} player${written.size === 1 ? '' : 's'}, but ` +
            `${missing.join(', ')} ${missing.length === 1 ? 'has' : 'have'} no scorecard row for ` +
            'this innings, so those figures could not be saved. That happens when the scorecard ' +
            'was created before they joined the squad — clear the squad and import it in one go.',
        ],
      }
    }

    toast.dismiss()
    ToastComponent(res.message || 'Squad and scores imported.', 'success')
    return { success: true }
  }

  const squadIds = squad.map((player) => player.player_id)
  const playerNameById = new Map(players.map((player) => [String(player.id), player.full_name]))

  const isSingleTeam = gameType === 1
  const teamLabel = isSingleTeam
    ? fixtureDetails?.team_name
    : (String(fixtureDetails?.team) === String(teamId) && fixtureDetails?.team_name) ||
      (String(fixtureDetails?.team2) === String(teamId) && fixtureDetails?.team2_name) ||
      ''
  const opponentName =
    String(fixtureDetails?.team) === String(teamId)
      ? fixtureDetails?.team2_name
      : fixtureDetails?.team_name

  const fixtureSummary = [
    fixtureDetails?.round ? `Round ${fixtureDetails.round}` : null,
    fixtureDetails?.grade,
    fixtureDetails?.match_type,
    !isSingleTeam && opponentName ? `vs ${opponentName}` : null,
  ]
    .filter(Boolean)
    .join(' • ')

  // match_code is the fixture's match type id: 28 is the two-innings format, 27 the one-day.
  const inningsOptions =
    Number(fixtureDetails?.match_code) === 28
      ? [
          { value: 1, label: '1st Innings' },
          { value: 2, label: '2nd Innings' },
        ]
      : [{ value: 1, label: '1st Innings' }]

  const scorecardPath = isSingleTeam
    ? `/scorecard/${fixtureId}/${fixtureDetails?.team}`
    : `/team-scorecard/${fixtureId}/${teamId}`

  return (
    <>
      <button type="button" className="ge-back-link" onClick={() => navigate('/manage-scores')}>
        <CIcon icon={cilArrowLeft} />
        Back to Manage Scores
      </button>

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1">{teamLabel || 'Team Squad'}</h4>
          <div className="text-medium-emphasis">
            {fixtureSummary || 'Build the squad for this fixture.'}
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <CButton color="primary" onClick={() => navigate(scorecardPath)}>
            <CIcon icon={cilSpreadsheet} className="me-1" />
            Scorecard
          </CButton>
          <CButton color="secondary" variant="outline" onClick={() => navigate('/squad-import')}>
            <CIcon icon={cilCloudUpload} className="me-1" />
            Import
          </CButton>
          <CButton
            color="danger"
            variant="outline"
            disabled={squad.length === 0}
            onClick={() => setShowConfirm(true)}
          >
            <CIcon icon={cilTrash} className="me-1" />
            Delete Squad
          </CButton>
        </div>
      </div>

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Players in Squad</div>
                <div className="fs-4 fw-semibold">{squad.length}</div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilPeople} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody>
              <div className="text-medium-emphasis small">Starts</div>
              <div className="fs-5 fw-semibold">{formatDate(fixtureDetails?.start_date)}</div>
              <div className="text-medium-emphasis small">
                {formatTime(fixtureDetails?.start_time)}
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody>
              <div className="text-medium-emphasis small">Ends</div>
              <div className="fs-5 fw-semibold">{formatDate(fixtureDetails?.end_date)}</div>
              <div className="text-medium-emphasis small">
                {formatTime(fixtureDetails?.end_time)}
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CollapsibleImportCard
        title="Import Squad from Spreadsheet"
        description="Upload a list of players — or a match export with their scores — map each row to a player, then import."
        open={importOpen}
        onToggle={() => setImportOpen((open) => !open)}
      >
        <SquadCsvImport
          players={players}
          squad={squad}
          scorecardColumns={scorecardColumns}
          columnsLoading={columnsLoading}
          innings={importInnings}
          inningsOptions={inningsOptions}
          onInningsChange={setImportInnings}
          onImport={importSquad}
          onCancel={() => setImportOpen(false)}
        />
      </CollapsibleImportCard>

      <CCard className="mb-3">
        <CCardBody>
          <h5 className="mb-3">Add Players</h5>
          <div className="ge-tabs" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`ge-tab${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="text-medium-emphasis small mb-3">{TAB_HINTS[activeTab]}</div>

          {activeTab === SELECT_TAB && <AddForm teamId={teamId} setPickedPlayer={loadPlayers} />}

          {activeTab === LIST_TAB && (
            <Table
              players={players}
              squadIds={squadIds}
              loading={loading}
              fixtureId={fixtureId}
              teamId={teamId}
              gameType={gameType}
              onChanged={loadPlayers}
            />
          )}

          {activeTab === IMAGE_TAB && (
            <>
              <div className="d-flex align-items-center gap-2 mb-3 text-medium-emphasis">
                <CIcon icon={cilImagePlus} />
                <span className="small">
                  A clear photo or screenshot of the team sheet works best.
                </span>
              </div>
              <ImportPlayerSquad
                fixtureId={fixtureId}
                teamId={teamId}
                setPickedPlayer={loadPlayers}
              />
            </>
          )}
        </CCardBody>
      </CCard>

      <CCard>
        <CCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h5 className="mb-1">Squad</h5>
              <div className="text-medium-emphasis small">
                Drag a row to change the batting order.
              </div>
            </div>
            <CBadge color="primary" shape="rounded-pill" className="px-3 py-2">
              {squad.length} {squad.length === 1 ? 'player' : 'players'}
            </CBadge>
          </div>
          <SquadPickedTable
            pickedPlayer={squad}
            fixtureId={fixtureId}
            teamId={teamId}
            onChanged={loadPlayers}
          />
        </CCardBody>
      </CCard>

      <Notify
        setShowConfirm={setShowConfirm}
        showConfirm={showConfirm}
        setHandleNo={setHandleNo}
        handleNo={handleNo}
        handleYes={handleYes}
        setHandleYes={setHandleYes}
        handleConfirm={handleConfirm}
        handleCancel={handleCancel}
        text="Are you sure you want to delete this squad? This also clears its scorecard."
      />
    </>
  )
}

export default TeamPlayer
